
// Utility functions for GTM/GA tracking

type PageViewParams = {
  path: string;
  title?: string;
  sendDirectly?: boolean;
}

/**
 * Track page view in Google Tag Manager
 * @param path - The current page path
 * @param title - The page title
 * @param sendDirectly - Whether to send directly to GA (bypass dataLayer)
 */
export const trackPageView = (path: string, title: string = document.title, sendDirectly: boolean = false) => {
  try {
    // Check if tracking is allowed based on cookie consent
    if (!isTrackingAllowed()) {
      return; // Exit if tracking isn't allowed by user consent
    }

    if (typeof window !== 'undefined') {
      // Use dataLayer push method (preferred)
      if (window.dataLayer && !sendDirectly) {
        window.dataLayer.push({
          event: 'pageview',
          page: {
            path,
            title
          }
        });
      }

      // Directly use gtag if available and sendDirectly is true
      if (window.gtag && sendDirectly) {
        window.gtag('event', 'page_view', {
          page_path: path,
          page_title: title,
          send_to: 'AW-16978595115'
        });
      }
    }
  } catch (error) {
    // Handle error silently
  }
};

/**
 * Track custom event in Google Tag Manager
 * @param eventName - Name of the event
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName: string, eventParams: Record<string, any> = {}) => {
  try {
    // Check if tracking is allowed based on cookie consent
    if (!isTrackingAllowed()) {
      return; // Exit if tracking isn't allowed by user consent
    }

    if (typeof window !== 'undefined') {
      // Use dataLayer push method (preferred)
      if (window.dataLayer) {
        window.dataLayer.push({
          event: eventName,
          ...eventParams
        });
      }

      // Direct gtag call as fallback
      if (window.gtag) {
        window.gtag('event', eventName, eventParams);
      }
    }
  } catch (error) {
    // Handle error silently
  }
};

/**
 * Track GTM-specific events with proper format
 * @param eventName - GTM event name (e.g., "gtm.init", "gtm.js", "dataLayer.initialized")
 * @param params - Additional GTM parameters
 */
export const trackGTMEvent = (eventName: string, params: Record<string, any> = {}) => {
  try {
    if (typeof window !== 'undefined' && window.dataLayer) {
      if (eventName === 'dataLayer.initialized') {
        // Special case for dataLayer.initialized event
        window.dataLayer.push({
          'event': eventName,
          ...params
        });
      } else if (eventName === 'gtm.js') {
        // Special case for gtm.js event
        window.dataLayer.push({
          'event': eventName,
          'gtm.start': params.start || new Date().getTime(),
          'gtm': {
            uniqueEventId: params.uniqueEventId || Math.floor(Math.random() * 1000000),
            start: params.start || new Date().getTime(),
            ...params
          }
        });
      } else {
        // Default format for other GTM events
        window.dataLayer.push({
          'event': eventName,
          'gtm': {
            uniqueEventId: params.uniqueEventId || Math.floor(Math.random() * 1000000),
            start: params.start || new Date().getTime(),
            ...params
          }
        });
      }
    }
  } catch (error) {
    // Handle error silently
  }
};

/**
 * Set consent mode for Google services with updated v2 parameters
 * @param settings - Consent settings with comprehensive privacy controls
 */
export const updateConsent = (settings: ConsentSettings) => {
  try {
    if (typeof window !== 'undefined') {
      // Update in GTM dataLayer first
      if (window.dataLayer) {
        // Main consent update event
        window.dataLayer.push({
          'event': 'consent_updated',
          'analytics_consent': settings.analytics ? 'granted' : 'denied',
          'marketing_consent': settings.marketing ? 'granted' : 'denied',
          'functional_consent': settings.functional ? 'granted' : 'denied',
          'ad_user_data_consent': settings.marketing ? 'granted' : 'denied',
          'ad_personalization_consent': settings.marketing ? 'granted' : 'denied'
        });

        // Specific consent grant events
        if (settings.analytics) {
          window.dataLayer.push({ 'event': 'analytics_consent_granted' });

          // New explicit behavioral analytics consent signal
          window.dataLayer.push({
            'event': 'consent_granted',
            'consent_type': 'behavioral_analytics',
            'uniqueEventId': Math.floor(Math.random() * 1000000)
          });

          // Send explicit behavioral analytics consent signal
          window.dataLayer.push({
            'event': 'behavioral_analytics_consent_granted',
            'timestamp': new Date().toISOString()
          });
        }

        if (settings.marketing) {
          window.dataLayer.push({ 'event': 'ad_consent_granted' });

          // New explicit advertising consent signals
          window.dataLayer.push({
            'event': 'consent_granted',
            'consent_type': 'ad_storage',
            'uniqueEventId': Math.floor(Math.random() * 1000000)
          });

          window.dataLayer.push({
            'event': 'consent_granted',
            'consent_type': 'ad_user_data',
            'uniqueEventId': Math.floor(Math.random() * 1000000)
          });

          window.dataLayer.push({
            'event': 'consent_granted',
            'consent_type': 'ad_personalization',
            'uniqueEventId': Math.floor(Math.random() * 1000000)
          });
        }
      }

      // Then update in gtag if available
      if (window.gtag) {
        // Updated consent parameters for Google Consent Mode v2
        window.gtag('consent', 'update', {
          'analytics_storage': settings.analytics_storage || (settings.analytics ? 'granted' : 'denied'),
          'ad_storage': settings.ad_storage || (settings.marketing ? 'granted' : 'denied'),
          'ad_user_data': settings.ad_user_data || (settings.marketing ? 'granted' : 'denied'),
          'ad_personalization': settings.ad_personalization || (settings.marketing ? 'granted' : 'denied'),
          'functionality_storage': settings.functionality_storage || (settings.functional ? 'granted' : 'denied'),
          'personalization_storage': settings.personalization_storage || (settings.functional ? 'granted' : 'denied'),
          'security_storage': settings.security_storage || 'granted', // Always recommended to be granted
          'wait_for_update': 500 // Wait for user consent update
        });
      }
    }
  } catch (error) {
    // Handle error silently
  }
};

/**
 * Check if user has granted analytics consent
 * @returns boolean indicating if analytics tracking is allowed
 */
const isTrackingAllowed = (): boolean => {
  try {
    if (typeof window !== 'undefined') {
      const savedConsent = localStorage.getItem('cookieConsent');
      if (savedConsent) {
        const consentData = JSON.parse(savedConsent);
        return consentData.settings?.analytics === true;
      }
    }
    return false; // Default to not tracking if consent status is unknown
  } catch (error) {
    return false; // Default to not tracking on error
  }
};

/**
 * Initialize analytics respecting user consent
 * Should be called early in the application lifecycle
 */
export const initializeAnalytics = () => {
  try {
    if (typeof window !== 'undefined') {
      // Initialize dataLayer if it doesn't exist
      window.dataLayer = window.dataLayer || [];

      // Signal that dataLayer is initialized
      trackGTMEvent('dataLayer.initialized');

      // Set default consent values - all denied by default
      if (window.gtag) {
        window.gtag('consent', 'default', {
          'analytics_storage': 'denied',
          'ad_storage': 'denied',
          'ad_user_data': 'denied',
          'ad_personalization': 'denied',
          'functionality_storage': 'denied',
          'personalization_storage': 'denied',
          'security_storage': 'granted' // Security-related storage is always granted
        });

        // Also send default consent to dataLayer for GTM
        window.dataLayer.push({
          'event': 'default_consent',
          'consent_status': {
            'analytics_storage': 'denied',
            'ad_storage': 'denied',
            'ad_user_data': 'denied',
            'ad_personalization': 'denied'
          }
        });
      }

      // Then apply saved consent if it exists
      const savedConsent = localStorage.getItem('cookieConsent');

      if (savedConsent) {
        const consentData = JSON.parse(savedConsent);
        const settings = consentData.settings;

        if (settings) {
          updateConsent({
            'analytics_storage': settings.analytics ? 'granted' : 'denied',
            'ad_storage': settings.marketing ? 'granted' : 'denied',
            'ad_user_data': settings.marketing ? 'granted' : 'denied',
            'ad_personalization': settings.marketing ? 'granted' : 'denied',
            'functionality_storage': settings.functional ? 'granted' : 'denied',
            'personalization_storage': settings.functional ? 'granted' : 'denied',
            'security_storage': 'granted',
            analytics: settings.analytics,
            marketing: settings.marketing,
            functional: settings.functional
          });
        }
      }

      // Explicit consent signals for GTM
      window.dataLayer.push({
        'event': 'gtm.init_consent',
        'gtm': {
          uniqueEventId: 6
        }
      });

      // Signal GTM consent initialization with specific flags
      window.dataLayer.push({
        'event': 'consent_initialization',
        'consent_flags': {
          'behavioral_analytics': 'denied',
          'ad_settings': 'denied',
          'requires_active_input': true,
          'consent_version': '2.0'
        }
      });
    }
  } catch (error) {
    // Handle error silently
  }
};

/**
 * Delete all cookies by category when consent is withdrawn
 */
const deleteCookiesByCategory = (category: string) => {
  try {
    const cookiesToDelete: Record<string, string[]> = {
      functional: ['ui-settings', 'language'],
      analytics: ['_ga', '_gid', '_gat', '_ga_*', '__utma', '__utmb', '__utmc', '__utmt', '__utmz'],
      marketing: ['ads_id', 'fbp', 'IDE', '_gcl_*', '_gac_*']
    };

    const domainParts = window.location.hostname.split('.');
    const domains = [];

    // Create variations of domain to handle cookies set on different domains
    for (let i = 0; i < domainParts.length - 1; i++) {
      domains.push('.' + domainParts.slice(i).join('.'));
    }

    cookiesToDelete[category]?.forEach(cookieName => {
      // Handle wildcard cookie names
      if (cookieName.includes('*')) {
        const prefix = cookieName.split('*')[0];
        document.cookie.split(';').forEach(cookie => {
          const cookiePart = cookie.trim().split('=')[0];
          if (cookiePart.startsWith(prefix)) {
            // Delete on all possible domains
            deleteCookie(cookiePart);
            domains.forEach(domain => {
              deleteCookie(cookiePart, domain);
            });
          }
        });
      } else {
        // Delete on all possible domains
        deleteCookie(cookieName);
        domains.forEach(domain => {
          deleteCookie(cookieName, domain);
        });
      }
    });
  } catch (error) {
    // Handle error silently
  }
};

/**
 * Helper function to delete a specific cookie
 */
const deleteCookie = (name: string, domain: string = '') => {
  const paths = ['/', '/en/', '/de/'];
  paths.forEach(path => {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=${path}${domain ? '; domain=' + domain : ''}`;
  });
};

// Re-export the deleteCookiesByCategory to use in CookieBanner
export { deleteCookiesByCategory };
